import {
  docsMap,
  forensic_docs_map,
  dea_missing_docs,
} from "./skorLiteDocumentMap";
import {
  getRequiredRetaPeriods,
  getRetaSlotIndex,
  isOtherPersonsProfile,
  isRetaDocName,
  RETA_ORDER,
} from "./retaRequirement";

function determineEmploymentType(tenant) {
  if (tenant?.employment_status === "student") return "student";
  if (tenant?.employment_status === "employed") return "employed";
  if (
    tenant?.employment_status === "self_employed" &&
    tenant?.employment_category === "freelancer"
  )
    return "freelancer";
  if (
    tenant?.employment_status === "self_employed" &&
    tenant?.employment_category === "ownACompany"
  )
    return "ownACompany";
  if (tenant?.employment_status === "retired") return "retired";
  if (tenant?.employment_status === "unemployed") return "unemployed";
  if (tenant?.employment_status === "public_servant") return "public_servant";
  if (tenant?.employment_status === "company_tenant") return "company_tenant";
  return "employed";
}

function getUploadedDocTypes(tenant) {
  const profileReports =
    tenant?.tenant_extra_field_id?.document_analysis?.profileReports ?? [];
  const payslipsWithPeriods = [];
  const retaPeriods = [];
  const uploadedNamesSet = new Set();
  let hasAnyPayslip = false;

  for (const profileReport of profileReports) {
    const documentBuckets = profileReport?.report?.documentBuckets;
    const documents = Array.isArray(documentBuckets)
      ? documentBuckets.flatMap((bucket) => bucket?.documents || [])
      : profileReport?.report?.matchedDocuments || [];

    for (const doc of documents) {
      if (doc.isDuplicate || !doc.documentName) continue;

      const rawName = doc.documentName;
      const mappedName = docsMap[rawName] || rawName;

      uploadedNamesSet.add(rawName.toLowerCase());
      if (mappedName !== rawName) {
        uploadedNamesSet.add(mappedName.toLowerCase());
      }

      if (isRetaDocName(rawName) && !isOtherPersonsProfile(profileReport)) {
        retaPeriods.push({
          period_year: doc.period_year,
          period_month: doc.period_month,
        });
      }

      if (rawName.toLowerCase().includes("payslip")) {
        hasAnyPayslip = true;
        if (doc.period_year !== undefined && doc.period_month !== undefined) {
          payslipsWithPeriods.push({
            period_year: doc.period_year,
            period_month: doc.period_month,
          });
        }
      }
    }
  }

  return {
    uploadedTypes: [...uploadedNamesSet],
    payslipsWithPeriods,
    hasAnyPayslip,
    retaPeriods,
  };
}

function flattenDocs(docs) {
  const result = [];
  for (const val of Object.values(docs)) {
    if (Array.isArray(val)) {
      result.push(...val);
    } else if (val && typeof val === "object") {
      result.push(...flattenDocs(val));
    }
  }
  return result;
}

function getDocsList(employmentType, isDea) {
  const requiredDocs = isDea
    ? dea_missing_docs[employmentType]
    : forensic_docs_map[employmentType];

  if (!requiredDocs) return { flat: [], flatLower: [] };

  const flat = flattenDocs(requiredDocs);
  const flatLower = flat.map((d) => d.toLowerCase());
  return { flat, flatLower };
}

function computeMissingPayslips(
  employmentType,
  uploadedTypes,
  payslipsWithPeriods,
  hasAnyPayslip,
  snapshotCreatedAt
) {
  if (employmentType !== "employed" && employmentType !== "public_servant") {
    return [];
  }

  const createdDate = snapshotCreatedAt
    ? new Date(snapshotCreatedAt)
    : new Date();
  const currentYear = createdDate.getFullYear();
  const currentMonth = createdDate.getMonth() + 1;

  const requiredPayslips = [];
  for (let i = 0; i < 3; i++) {
    let year = currentYear;
    let month = currentMonth - i - 1;
    if (month <= 0) {
      month += 12;
      year -= 1;
    }
    requiredPayslips.push({ year, month });
  }

  const payslipDocNames = [];

  if (hasAnyPayslip && payslipsWithPeriods.length > 0) {
    const uploadedPayslipKeys = payslipsWithPeriods.map(
      (p) => `${p.period_year}-${p.period_month}`
    );
    requiredPayslips.forEach((payslip) => {
      const key = `${payslip.year}-${payslip.month}`;
      if (!uploadedPayslipKeys.includes(key)) {
        payslipDocNames.push(`payslip - ${payslip.month}/${payslip.year}`);
      }
    });
  } else {
    requiredPayslips.forEach((payslip) => {
      payslipDocNames.push(`payslip - ${payslip.month}/${payslip.year}`);
    });
  }

  return payslipDocNames;
}

export function getMissingDocsFromSnapshot(
  tenantData,
  isDea,
  snapshotCreatedAt
) {
  if (!tenantData) {
    return { missingDocs: [], missingCount: 0 };
  }

  const employmentType = determineEmploymentType(tenantData);
  const { uploadedTypes, payslipsWithPeriods, hasAnyPayslip, retaPeriods } =
    getUploadedDocTypes(tenantData);
  // Freelancers: a RETA receipt fills the slot of its month, counted from
  // the snapshot date like the payslips.
  if (employmentType === "freelancer") {
    const requiredRetaPeriods = getRequiredRetaPeriods(
      snapshotCreatedAt ? new Date(snapshotCreatedAt) : new Date()
    );
    retaPeriods.forEach((period) => {
      const slotIndex = getRetaSlotIndex(period, requiredRetaPeriods);
      if (slotIndex >= 0)
        uploadedTypes.push(RETA_ORDER[slotIndex].toLowerCase());
    });
  }
  const { flatLower: docsListLower } = getDocsList(employmentType, isDea);

  const identificationDocs = [
    "passport",
    "national_identity_card_front",
    "national_identity_card_back",
    "residence_permit_front",
    "residence_permit_back",
  ];

  const missingDocs = docsListLower.filter((doc) => {
    if (identificationDocs.includes(doc)) {
      return false;
    }

    if (
      doc === "payslip" ||
      doc === "payslip month 1" ||
      doc === "payslip month 2" ||
      doc === "payslip month 3"
    ) {
      return false;
    }

    return !uploadedTypes.includes(doc);
  });

  const missingPayslips = computeMissingPayslips(
    employmentType,
    uploadedTypes,
    payslipsWithPeriods,
    hasAnyPayslip,
    snapshotCreatedAt
  );
  missingPayslips.forEach((p) => missingDocs.push(p.toLowerCase()));

  const missingProtectedHousingDocs = [];
  if (tenantData?.protected_housing && !isDea) {
    const publicHousingDocs =
      tenantData?.tenant_extra_field_id?.public_housing_docs || [];

    const residencyDoc = "Certificate of Residency Registration";
    const noSpecialCircumstances = "No special circumstances";

    const baseDocs = publicHousingDocs.filter(
      (doc) =>
        doc &&
        doc !== residencyDoc &&
        doc !== noSpecialCircumstances &&
        doc !== "no"
    );

    let requiredProtectedDocs = [];
    if (baseDocs.length > 0) {
      requiredProtectedDocs = [...new Set([...baseDocs, residencyDoc])];
    } else if (publicHousingDocs.includes(noSpecialCircumstances)) {
      requiredProtectedDocs = [residencyDoc];
    }

    requiredProtectedDocs.forEach((doc) => {
      if (!uploadedTypes.includes(doc.toLowerCase())) {
        missingProtectedHousingDocs.push(doc.toLowerCase());
      }
    });
  }

  missingDocs.push(...missingProtectedHousingDocs);

  return { missingDocs, missingCount: missingDocs.length };
}
